import { ref, reactive, PropType } from 'vue';
import { highlightDomElement } from '@orion.ui/orion';
import BaseSetupService from '../BaseSetupService';
import TempWorkerEntity from '@/entity/temp-worker/TempWorkerEntity';
import useOrderService from '@/services/OrderService';
import useLanguageService from '@/services/LanguageService';
import type OrderEntity from '@/entity/order/OrderEntity';
import TempWorkerCandidateEntity from '@/entity/temp-worker/TempWorkerCandidateEntity';
import useOrderClientProposalService from '@/services/OrderClientProposalService';
import useCandidateService from '@/services/CandidateService';
import useRouterHistoryService from '@/services/RouterHistoryService';
import useAgencyCandidateService from '@/services/agency-candidate/AgencyCandidateService';


type Props = SetupProps<typeof TempWorkerAsideDetailsSetupService.props>

export default class TempWorkerAsideDetailsSetupService extends BaseSetupService<Props> {
	static readonly props = {
		isValidatedCandidate: Boolean,
		id: {
			type: [Number, String],
			required: true as const,
		},
		tab: {
			type: String,
			default: undefined,
		},
		order: {
			type: Object as PropType<OrderEntity>, // type OrderRegistryItem
			default: undefined,
		},
		orderClientProposal: {
			type: Object as PropType<IOrderClientProposalWithUserModel>, // type OrderRegistryItem
			default: undefined,
		},
		highlightElement: {
			type: String as PropType<'taskSection' | 'commentSection'>,
			default: undefined,
		},
	};

	protected readonly entity = this.self.isAgency()
		? new TempWorkerEntity()
		: new TempWorkerCandidateEntity();

	private _aside?: OrionAside;
	private readonly state = reactive({
		activeTab: 'general',
		availabilities: undefined as Undef<IAvailabilityModel>,
		unavailabilities: [] as IUnavailabilityModel[],
		allreadyRequestedDocuments: [] as IRequestedInformationForAgencyModel[],
		allJustificatories: [] as IJustificatoryModel[],
	});

	readonly _refs = {
		taskSection: ref<HTMLElement>(),
		commentSection: ref<HTMLElement>(),
	};

	orderService = useOrderService();
	userLanguageService = useLanguageService();

	get id () { return +this.props.id; }
	get tempWorker () { return this.entity; };

	get activeTab () { return this.state.activeTab; }
	set activeTab (val) { this.state.activeTab = val; }


	constructor (props: Props, _aside?: OrionAside) {
		super(props);
		this._aside = _aside;
	}

	protected async onBeforeMountAsync () {
		this._aside?._loader()?.show(`Chargement des données...`);

		await this.getDetailsAsync();

		if (this.self.isAgency() && this.isForAgency()) {
			await Promise.all([
				this.tempWorker.loadAllJustificatoriesAsync(),
				this.tempWorker.loadAlreadyRequestedDocumentsAsync(),
				this.tempWorker.loadAvailabilityAsync(),
				this.tempWorker.loadUnavailabilitiesAsync(),
			]);
		}

		this._aside?._loader()?.hide();
	}

	protected onMounted () {
		this._aside?.open();
		this._aside?.bus.on('leave-end', () => {
			if (this.route.name === 'TempWorkerDetails') {
				if (useRouterHistoryService().previousRoute?.name === 'TempWorker') {
					this.router.push(useRouterHistoryService().previousRoute);
				} else {
					this.router.push({ name: 'TempWorker' });
				}
			}
		});
	}

	protected onUnmounted () {
		this._aside?.close();
	}


	isForAgency (): this is { tempWorker: TempWorkerEntity } {
		return this.tempWorker instanceof TempWorkerEntity;
	}

	isForClient (): this is { tempWorker: TempWorkerCandidateEntity } {
		return this.tempWorker instanceof TempWorkerCandidateEntity;
	}

	async getDetailsAsync () {
		this._aside?._loader()?.show(`Chargement des données...`);

		if (this.self.isAgency() && this.isForAgency()) {
			const data = await useAgencyCandidateService().getDetailsAsync(this.id);

			this.tempWorker.assignData(data);

			useCandidateService().setCandidateRating({
				candidateId: this.tempWorker.id,
				rating: data.rating,
				numberOfRating: data.numberOfRating,
			});
		} else if (this.isForClient()) {
			if (this.props.isValidatedCandidate && this.props.order?.id) {
				const data = await useOrderService().getTempworkerDetailsAsync(this.props.order.id, this.id);
				if (data.tempWorkerUser) this.tempWorker.assignData(data.tempWorkerUser);
			} else if (this.props.orderClientProposal) {
				const data = await useOrderClientProposalService().getDetailsAsync(this.props.orderClientProposal.id);
				if (data.tempWorkerUser) this.tempWorker.assignData(data.tempWorkerUser);
			}
		}

		this._aside?._loader()?.hide();
		this.checkForDomElementHighlight();
	}

	async acceptCandidateAsync () {
		if (!this.props.orderClientProposal) return;

		await useOrderClientProposalService().acceptProposalAsync([this.props.orderClientProposal]);
	}

	async declineCandidateAsync () {
		if (!this.props.orderClientProposal) return;

		await useOrderClientProposalService().declineProposalAsync([this.props.orderClientProposal]);
	}

	checkForDomElementHighlight () {
		const highlightElement = this.props.highlightElement ?? this.route.query.highlightElement as keyof typeof this._refs;
		if (highlightElement) {
			setTimeout(() => {
				const element = this._refs[highlightElement].value;
				if (element) {
					highlightDomElement(element);
					element?.scrollIntoView({
						behavior: 'smooth',
						block: 'center',
					});
				}
			}, 500);
		}
	}
}
